// Registers a renderer for the backend `generative-ui-artifact(title, html)` tool
// call. Every call (including the first) replaces the panel's revision —
// mirrors https://www.assistant-ui.com/examples/artifacts. Mounted once,
// globally, inside AssistantRuntimeProvider (see ChatWorkspace.tsx) rather
// than per-message, since its job is a side effect (writing into
// useArtifactPanelStore) plus a minimal inline chip — not the panel itself.
//
// The side effect that drives the panel (pushRevision/updateLatestRevision)
// lives in `ArtifactWatcher` below, NOT in the per-tool-call render callback
// (`UpdateArtifactCall`) registered via `useAssistantToolUI`. That callback
// is only invoked while ChatMessage's `ToolGroupContent` (a Radix
// `CollapsibleContent`) is mounted, and Radix never mounts a `Collapsible`'s
// content on first render when it starts closed — `ToolGroupRoot` opens only
// while the owning message is still streaming (`shared/ui/tool-group.tsx`'s
// `useAutoCollapse`). A call that finishes before the group is ever expanded
// (the common case for a fast/erroring tool call, or a persisted message
// loaded already-complete) never mounts `UpdateArtifactCall` at all, so its
// effect never fires and the panel never auto-opens — confirmed live: the
// panel stayed closed until manually expanding "Used N tools". `ArtifactWatcher`
// instead reads the thread's messages directly (independent of what
// ChatMessage chooses to render/collapse), so the panel opens the moment an
// `generative-ui-artifact` call appears, regardless of message UI state.
import { useEffect, useRef } from 'react';
import { FileCode2 } from 'lucide-react';
import { useAssistantToolUI, useAuiState } from '@assistant-ui/react';
import type { ToolCallMessagePartProps } from '@assistant-ui/react';
import { useArtifactPanelStore } from '@/shared/stores/artifactPanel';

interface UpdateArtifactArgs {
  title?: string;
  html?: string;
}

interface ArtifactToolResult {
  title?: string;
  side_pane?: { title?: string; html?: string };
}

// Exported for testing — rendered directly rather than through the
// `useAssistantToolUI` registration, which requires a full runtime provider.
// Presentation only now — see the module comment for why the panel-store
// side effect moved to `ArtifactWatcher`.
export function UpdateArtifactCall({ status }: ToolCallMessagePartProps<UpdateArtifactArgs>) {
  return (
    <div className="mb-2 flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/40 p-2 text-xs text-muted-foreground">
      <FileCode2 className="size-3.5 shrink-0" />
      <span>{status.type === 'running' ? 'Updating the artifact panel…' : 'Updated the artifact panel'}</span>
    </div>
  );
}

/**
 * Scans every message's parts for `update_artifact` tool calls and drives
 * the per-thread panel store directly (pure — exported for testing).
 *
 * The `html`-truthy guard on repeat calls was added while the backend
 * persisted a completed tool call's `arguments` as `{}` (see
 * `useAgenthubExternalRuntime.ts`'s post-run refetch, which replays
 * persisted history over the live thread) — without it, that reconciliation
 * would replay this same toolCallId with empty args and blank out a
 * revision that had already rendered real content. The backend gap itself
 * is now fixed (see `services/invocation/event_collector.py` — the final
 * `ToolUseBlock` is always re-emitted with real args before persistence), so
 * `html` should never actually be empty on a repeat call anymore. The guard
 * stays as harmless defense-in-depth: if that persistence path ever
 * regresses, this still prevents a rendered preview from going blank.
 */
// eslint-disable-next-line react-refresh/only-export-components -- pure test helper colocated with the registered tool UI.
export function collectArtifactUpdates(
  messages: readonly { content: readonly { type: string; toolName?: string; toolCallId?: string; args?: unknown; result?: unknown }[] }[],
  seenCallIds: Set<string>,
  pushRevision: (title: string, html: string) => void,
  updateLatestRevision: (title: string, html: string) => void,
): void {
  for (const message of messages) {
    for (const part of message.content) {
      if (part.type !== 'tool-call' || part.toolName !== 'generative-ui-artifact' || !part.toolCallId) continue;
      const args = (part.args ?? {}) as UpdateArtifactArgs;
      const result = (part.result ?? {}) as ArtifactToolResult;
      const title = result.side_pane?.title ?? result.title ?? args.title ?? '';
      const html = result.side_pane?.html ?? args.html ?? '';
      if (!seenCallIds.has(part.toolCallId)) {
        seenCallIds.add(part.toolCallId);
        pushRevision(title, html);
      } else if (html) {
        updateLatestRevision(title, html);
      }
    }
  }
}

function ArtifactWatcher({ threadId }: { threadId: string }) {
  const messages = useAuiState((s) => s.thread.messages);
  const pushRevision = useArtifactPanelStore((s) => s.pushRevision);
  const updateLatestRevision = useArtifactPanelStore((s) => s.updateLatestRevision);
  const seenCallIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    collectArtifactUpdates(
      messages,
      seenCallIds.current,
      (title, html) => pushRevision(threadId, title, html),
      (title, html) => updateLatestRevision(threadId, title, html),
    );
  }, [messages, threadId, pushRevision, updateLatestRevision]);

  return null;
}

export function UpdateArtifactToolUI({ threadId }: { threadId: string }) {
  useAssistantToolUI({ toolName: 'generative-ui-artifact', render: UpdateArtifactCall, display: 'standalone' });
  return <ArtifactWatcher threadId={threadId} />;
}
